/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 */

/** Per-tenant storefront branding, returned by GET /api/storefront/tenant. */
export interface Tenant {
  ownerId: string;
  subdomain?: string;
  customDomain?: string;
  displayName?: string;
  logoUrl?: string;
  bannerImageUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  tagLine?: string;
  aboutText?: string;
  /** Terms & Conditions — rich text (HTML); base/fallback value. */
  terms?: string;
  /** Multilingual variants (JSON localized maps {lang: value}) of the Design-tab text fields.
   *  aboutTextJson + termsJson hold rich text (HTML). */
  tagLineJson?: string | null;
  aboutTextJson?: string | null;
  termsJson?: string | null;
  contactAddressJson?: string | null;
  /** Contact block (Design editor) — shown first on the Contact page. */
  contactEmail?: string | null;
  contactPhone?: string | null;
  contactAddress?: string | null;
  /** Design page fields (myezwheels storefront Design editor). */
  faviconUrl?: string;
  backgroundImageUrl?: string;
  videoUrl?: string;
  customCss?: string;
  /** JSON array of multilingual Section[] — Content tab. */
  contentJson?: string;
  /** JSON array of multilingual Section[] — Sections tab. */
  sectionsJson?: string;
  /** Main tab — how this company's fleet is listed here: one card per make+model ('models'),
   *  every individual car ('cars'), or 'both' = the visitor picks with the Cars|Models switch. */
  vehicleDisplayMode?: 'models' | 'cars' | 'both';
  /** GUI/i18n codes this storefront has been translated into (e.g. ["en","es","zh-Hant"]). */
  translatedLanguages?: string[];
  isActive: boolean;
}

/** A car listing as returned by the storefront search/detail endpoints. */
export interface Car {
  id: string;
  title: string;
  description?: string;
  imageURL?: string;
  brand?: string;
  model?: string;
  year?: number;
  ownerId?: string;
  /** The rental company (owner) offering the car. */
  companyName?: string;
  typeName?: string;
  typeColor?: string;
  /** Vehicle segment from CarCategories (SUV, Premium, Sports, …). */
  categoryId?: string;
  categoryName?: string;
  /** Multilingual category name (JSON localized map); the storefront renders it per UI language. */
  categoryNameJson?: string | null;
  categoryPosition?: number;
  pricePerWeek: number;
  priceFullTimePerWeek?: number;
  // Per-period rate tiers (like HQ daily/weekly/monthly). 0 = tier not offered.
  priceDaily?: number;
  priceWeekly?: number;
  priceMonthly?: number;
  rentalType: number;
  /** Period the stored price is in: 0=Week, 1=Day, 2=Month. */
  pricingPeriod?: number;
  rating?: number;
  city?: string;
  state?: string;
  latitude?: number;
  longitude?: number;
}

export interface CarSearchRequest {
  city?: string;
  state?: string;
  latitude: number;
  longitude: number;
  typesIds?: string[];
  dateFrom: string;
  dateTo: string;
  lastDate?: string;
  rentalType: number;
  /** Filter by pricing period: 0=Week, 1=Day, 2=Month. Omit = any. */
  pricingPeriod?: number;
  distance: number;
  /** Filter by a shared pickup location (CommonLocations.Id) — from getLocations(). Omit = any. */
  commonLocationId?: string;
}

export interface StorefrontLocation {
  id: string;
  name: string;
  city?: string | null;
  state?: string | null;
  kind?: string | null;
  carCount: number;
}

/** A tenant office shown on the Contact page (only offices that have an email or phone). */
export interface StorefrontContactLocation {
  id: string;
  name: string;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  country?: string | null;
  email?: string | null;
  phone?: string | null;
}

/** RentalType bit flags (mirror of backend). */
export const RentalType = {
  Week: 1,
  Day: 2,
  Shift: 4,
  Month: 8,
} as const;

export interface DomainStatus {
  fullDomain: string;
  validationState: string;
  deploymentStatus: string;
  certificateState: string;
  isReady: boolean;
  errorMessage?: string;
}

export interface OwnerSiteState {
  result: Tenant;
  subdomainStatus?: string;
  customDomainStatus?: string;
}

/** Authenticated renter (subset of the backend AuthResponse). */
export interface AuthUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  token: string;
  tokenExpired?: string;
}

export interface OrderBookingRequest {
  carId: string;
  dateFrom: string; // ISO 8601 UTC
  dateTo: string; // ISO 8601 UTC
  rentalType: number;
}

export interface BookingResult {
  ok: boolean;
  bookingId?: string;
  chatId?: string;
  /** Backend reason code (non-zero = handled failure, e.g. no card / profile incomplete). */
  reason?: number;
  message?: string;
}

/** Booking config for a car's booking page (GET storefront/booking-info/{carId}). */
export interface BookingInfo {
  found: boolean;
  carId?: string;
  ownerId?: string;
  price: number;
  securityDeposit: number;
  /** 0=Week, 1=Day, 2=Month. */
  pricingPeriod: number;
  // Per-period rate tiers (like HQ daily/weekly/monthly). 0 = tier not offered. The client picks the tier
  // matching the chosen rental length; price/pricingPeriod above are the representative rate.
  priceDaily?: number;
  priceWeekly?: number;
  priceMonthly?: number;
  minimumReservationDays: number;
  /** True when the tenant owner books through a fleet plugin (route to POST storefront/booking). */
  usesBookingPlugin: boolean;
  bookingPlatformId?: string | null;
  /** True when the owner collects via a booking plugin that is currently unavailable — booking is
   * blocked (native is forbidden for this owner). */
  bookingPluginUnavailable?: boolean;
  /** True when the owner requires a signed rental agreement. */
  requiresAgreement?: boolean;
  /** The owner's active additional services (extras) the renter can add to the booking. */
  services?: AdditionalServiceOption[];
  /** The owner's display currency (ISO code, e.g. "USD"/"EUR"); drives the price symbol. */
  currency?: string;
}

/** One additional service (extra) offered on the booking page. */
export interface AdditionalServiceOption {
  additionalServiceId: string;
  name: string;
  /** Base (English) description; storefront prefers the localized descriptionJson. */
  description?: string;
  /** Multilingual {lang:text} JSON for the name; storefront picks the active language, falls back to name. */
  nameJson?: string;
  /** Multilingual {lang:text} JSON for the description. */
  descriptionJson?: string;
  price: number;
  /** AddOnPricing cadence: per_day/per_hour/per_week/per_month/per_minute/percent/one_time. */
  pricingType: string;
  isMandatory: boolean;
  currency?: string;
}

/** Resolve the first available car of a make/model (POST storefront/book-model). */
export interface ModelResolveRequest {
  make?: string;
  model: string;
  /** Required on the main domain to scope across owners. */
  city?: string;
  /** Optional: restrict to one rental company (owner) — set after the company picker. */
  ownerId?: string;
  dateFrom: string;
  dateTo: string;
}

/** A rental company offering a model (storefront/model-companies) for the company picker. */
export interface ModelCompany {
  ownerId: string;
  companyName: string;
  logoUrl?: string;
  address?: string;
  availableCount: number;
  minPrice: number;
  pricingPeriod: number;
  host?: string;
}

/** Result of a plugin-routed booking (POST storefront/booking). */
export interface PluginBookingResult {
  ok: boolean;
  flow?: string;
  platformId?: string;
  bookingId?: string;
  externalBookingId?: string;
  bookingNumber?: string;
  /** Platform status; "paid" means the platform charged a card on file. */
  status?: string;
  /** Hosted payment link the renter opens to pay the rental company (add card + pay). */
  paymentLink?: string;
  totalAmount?: number;
  message?: string;
}

/** Booking lifecycle states (mirror of backend Aegis.Common.Enums.BookingStatus). */
export const BookingStatus = {
  Init: 0,
  PendingApproval: 1,
  CompletedEarly: 2,
  RequestExpired: 3,
  RequestDenied: 4,
  ApprovedWaitingForInsuranceApproval: 5,
  Approved: 6,
  Completed: 7,
  Draft: 8,
  DepositFailed: 9,
  PaymentFailed: 10,
  RefundFailed: 11,
  TollCardMissing: 12,
  WaitingNewDriverInfo: 14,
  WaitingForAgreement: 15,
  WaitingForCC: 16,
  Canceled: 17,
} as const;

/** How a booking can be cancelled (mirror of backend CancelType). */
export const CancelType = {
  None: 0,
  Simple: 1,
  Deposit: 2,
  Payment: 3,
} as const;

/** One of the signed-in renter's bookings (GET Booking/get-renter-bookings → BookingsDTO). */
export interface RenterBooking {
  bookingId: string;
  carId: string;
  carTitle?: string;
  carBrand?: string;
  carModel?: string;
  carYear?: number;
  carImage?: string;
  carPlateNumber?: string;
  dateFrom: string;
  dateTo: string;
  price: number;
  status: number;
  rentalType: number;
  agreementPdfUrl?: string;
}

/** A rental company the signed-in driver keeps a card set with (GET tollPayment/my-card-owners). */
export interface CardOwner {
  ownerId: string;
  name: string;
}

/** A renter's saved card (GET tollPayment/get-cards-byId/{userId}). */
export interface SavedCard {
  brand?: string;
  last4?: string;
  expirationMonth?: number;
  expirationYear?: number;
  isActive?: boolean;
  stripePaymentMethodId?: string;
}
